import { ILocationResponse } from '@kcftech/prometheustypescriptapiclient';
import { Dropdown, DropdownItemRenderer } from 'components/dropdown/Dropdown';
import {
    onDropdownItemLinkClick,
    SimpleDropdownLabelRenderer,
    useDropdownLabelRenderer,
} from 'components/dropdown/shared';
import { DropdownItemLink } from 'components/dropdown/styled';
import { FavoriteButton } from 'components/favorites/FavoriteButton';
import {
    infiniteLocationsDataSourceKey,
    useInfiniteLocationDataSource,
} from 'custom_hooks/data_hooks/data_source/locationsInfiniteDataSource';
import { useCallback, useEffect } from 'react';
import { getId } from 'utils/data';
import { getLocationLandingPageRoute } from 'utils/RouteHelper';

export type LocationDropdownProps = Readonly<{
    /**
     * The currently selected location.
     */
    value: ILocationResponse | undefined;
    /**
     * Callback to call when the selected location should change.
     */
    onChange: (location: ILocationResponse) => void;
    /**
     * Placeholder text for the search field.
     */
    placeholder?: string;
    /**
     * Narrow down search results to a single corporation.
     */
    corporationId?: string;
    className?: string;
    disabled?: boolean;
    popupWidth?: string;
    renderLabel?: SimpleDropdownLabelRenderer<ILocationResponse>;
    interactionMode?: 'link' | 'select';
    showFavoriteIcon?: boolean;
}>;

/**
 * Dropdown which allows selecting a location from a list of available locations.
 */
export function LocationDropdown({
    className,
    value,
    onChange,
    placeholder = 'Select a Location',
    corporationId,
    disabled,
    popupWidth,
    renderLabel: renderLabelProp,
    interactionMode = 'link',
    showFavoriteIcon = false,
}: LocationDropdownProps) {
    const shouldShowCorporation = corporationId === undefined || corporationId === '';
    const renderLabel = useDropdownLabelRenderer({
        defaultRenderer: ({ value: selectedValue }) =>
            shouldShowCorporation
                ? `${selectedValue.Corporation.CorporationName} / ${selectedValue.Name}`
                : selectedValue.Name,
        overrideRenderer: renderLabelProp,
        placeholder,
    });

    const renderItem: DropdownItemRenderer<ILocationResponse> = useCallback(
        ({ item }) => {
            if (shouldShowCorporation) {
                return `${item.Corporation.CorporationName} / ${item.Name}`;
            }

            if (interactionMode === 'link') {
                return (
                    <DropdownItemLink
                        onClick={onDropdownItemLinkClick}
                        to={getLocationLandingPageRoute(item.Id)}
                    >
                        {item.Name}
                        {showFavoriteIcon && (
                            <FavoriteButton favoriteItem={item} favoriteType="Location" />
                        )}
                    </DropdownItemLink>
                );
            }

            return (
                <div className="flex justify-between">
                    {item.Name}
                    {showFavoriteIcon && (
                        <FavoriteButton favoriteItem={item} favoriteType="Location" />
                    )}
                </div>
            );
        },
        [interactionMode, shouldShowCorporation, showFavoriteIcon]
    );

    const {
        results,
        filter,
        setFilter,
        isLoading,
        hasNext,
        loadNext,
    } = useInfiniteLocationDataSource({
        name: infiniteLocationsDataSourceKey,
        defaultFilter: { corporationId },
        defaultSort: [
            { field: 'CorporationName', dir: 'asc' },
            { field: 'Name', dir: 'asc' },
        ],
    });

    useEffect(() => setFilter((current) => ({ ...current, corporationId })), [
        corporationId,
        setFilter,
    ]);

    const onFilterChange = (nameSearch: string) =>
        setFilter((current) => ({ ...current, name: nameSearch }));

    return (
        <Dropdown<ILocationResponse>
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            dataKey={getId}
            renderLabel={renderLabel}
            renderItem={renderItem}
            value={value}
            onChange={onChange}
            filterable={{
                filterText: filter.name || '',
                onChange: onFilterChange,
                placeholder: 'Search Locations',
            }}
            trigger={{ className }}
            popup={{ width: popupWidth }}
            isDisabled={disabled}
        />
    );
}
